import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@material-ui/core/styles';
import { colors } from '../../../theme/color-palette';
import { useMediaQuery } from '@mui/material';
import {
	DATA_NOT_AVAILABLE,
	ERROR_SCREEN_MSG_STORE,
	chartMetricType,
} from '../../../constants/constant';
import ErrorScreenMessage from '../../../components/ErrorScreenMessage/ErrorScreenMessage.js';
import analytics from '../../../helper/analytics';
import tracker from '../../../constants/tracker';

const { PURPLE, FONT_COLOR } = colors;

const useStyles = makeStyles((theme) => ({
	indicator: {
		down: '0px',
		borderRadius: '100px 100px 0px 0px',
	},
	tabName: {
		fontSize: '14px',
		[theme.breakpoints.down('sm')]: {
			fontSize: '12px',
		},
		color: FONT_COLOR,
		marginBottom: '4px',
		lineHeight: '19.46px',
	},
	active: {
		fontWeight: '400',
		fontFamily: 'Bogle-Medium',
	},
	inActive: {
		fontWeight: '400',
		fontFamily: 'Bogle-Light',
	},
	valuesTab: {
		fontSize: '24px',
		[theme.breakpoints.down('sm')]: {
			fontSize: '18px',
		},
		lineHeight: '33.36px',
		color: FONT_COLOR,
		fontWeight: 500,
		marginBottom: 0,
	},
}));

const AntTab = styled((props) => (
	<Tab
		style={{ padding: '12px 16px', justifyContent: 'center' }}
		disableRipple
		{...props}
	/>
))(({ theme }) => ({
	textTransform: 'none',
	minWidth: 0,
	marginRight: theme.spacing(1),
	fontSize: 14,
	display: 'flex',
	alignItems: 'start',
	padding: '0px 15px 0px 0px',
	margin: '0px 9px 0px 0px',
	'&:hover': {
		backgroundColor: '#F1F1F1',
		cursor: 'pointer',
	},
}));

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

function a11yProps(index) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
		'data-testId': `simple-tab-${index}`,
	};
}

export default function GenerateTabs(props) {
	const {
		createTabs,
		childComponent,
		setTabValue,
		tabValue,
		updatedWidth,
		data = { isError: [false, false, false] },
		type,
	} = props;
	const metricName = chartMetricType[tabValue];
	const chartdata = data[metricName];
	const matches = useMediaQuery('(max-width:768px)');
	const ErrorCode = _?.isEmpty(data) ? 0 : data?.isError[tabValue];
	const errorMsg =
		ErrorCode === 0 ? DATA_NOT_AVAILABLE : ERROR_SCREEN_MSG_STORE;
	const classes = useStyles({});

	const handleChange = (event, newValue) => {
		setTabValue(newValue);
		adobeTrackWosClick(newValue);
	};

	const adobeTrackWosClick = (tabValue) => {
		let tabName = `${metricName}_${type}_${createTabs[tabValue].tabName}`;
		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: tabName,
		});
	};

	const getChartSubtitle = (tabValue) => {
		if (tabValue === 0) {
			return 'Net Sales';
		} else if (tabValue === 1) {
			return 'Units';
		}
		return '';
	};

	return (
		<>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs
					classes={{
						indicator: classes.indicator,
					}}
					sx={{ padding: '0px 16px 0px 16px', minHeight: '65px' }}
					value={tabValue}
					variant="scrollable"
					selectionFollowsFocus
					scrollButtons={matches}
					allowScrollButtonsMobile={matches}
					onChange={handleChange}
					TabIndicatorProps={{
						style: { background: PURPLE, height: 4 },
					}}
					aria-label="donut chart tabs">
					{createTabs?.map((item, idx) => {
						return (
							<AntTab
								key={idx}
								label={
									<>
										<Box
											className={`${
												item.active ? classes.active : classes.inActive
											} ${classes.tabName}`}>
											{item.tabName}
										</Box>
										<Box
											className={`${
												item.active ? classes.active : classes.inActive
											} ${classes.valuesTab}`}>
											{item.tabValue}
											{item.active && item.diff}
										</Box>
									</>
								}
								{...a11yProps(idx)}
							/>
						);
					})}
				</Tabs>
			</Box>
			{createTabs?.map((item, idx) => {
				return (
					<>
						{chartdata?.channelBreakdownData?.data?.length === 0 ? (
							<>
								<TabPanel value={tabValue} index={idx} key={idx}>
									{
										<ErrorScreenMessage
											errorMsg={`${getChartSubtitle(tabValue)} ${errorMsg}`}
											isInsideTabPanel={true}
										/>
									}
								</TabPanel>
							</>
						) : (
							<TabPanel
								value={tabValue}
								index={idx}
								key={idx + `${updatedWidth}`}>
								{}
								{childComponent}
							</TabPanel>
						)}
					</>
				);
			})}
		</>
	);
}

GenerateTabs.propTypes = {
	createTabs: PropTypes.arrayOf(String),
	childComponent: PropTypes.element,
	setTabValue: PropTypes.func,
	tabValue: PropTypes.number,
	updatedWidth: PropTypes.number,
	data: PropTypes.object,
	type: PropTypes.string,
};
